import React, { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import CameraswitchRoundedIcon from '@mui/icons-material/CameraswitchRounded';

// Captura pela câmera do navegador (celular ou webcam) usando getUserMedia.
const CameraDialog = ({ open, onClose, onCapture }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState('user');
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let ativo = true;
    setErro('');
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode, width: { ideal: 1280 }, height: { ideal: 1280 } }, audio: false })
      .then((stream) => {
        if (!ativo) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setErro('Não foi possível acessar a câmera. Verifique a permissão do navegador ou escolha uma foto da galeria.'));
    return () => {
      ativo = false;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [open, facingMode]);

  const capturar = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const lado = Math.min(video.videoWidth, video.videoHeight);
    const canvas = document.createElement('canvas');
    canvas.width = lado;
    canvas.height = lado;
    const ctx = canvas.getContext('2d');
    if (facingMode === 'user') {
      ctx.translate(lado, 0);
      ctx.scale(-1, 1); // mesma orientação do preview espelhado
    }
    ctx.drawImage(video, (video.videoWidth - lado) / 2, (video.videoHeight - lado) / 2, lado, lado, 0, 0, lado, lado);
    canvas.toBlob((blob) => {
      if (blob) onCapture(new File([blob], `foto-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.9);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Tirar foto</DialogTitle>
      <DialogContent>
        {erro ? (
          <Alert severity="warning">{erro}</Alert>
        ) : (
          <Box sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden', bgcolor: '#000', aspectRatio: '1' }}>
            <Box component="video" ref={videoRef} autoPlay playsInline muted sx={{ width: '100%', height: '100%', objectFit: 'cover', transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }} />
            <Box sx={{ position: 'absolute', inset: '12%', borderRadius: '50%', border: '2px dashed rgba(255,255,255,0.6)', pointerEvents: 'none' }} />
            <IconButton onClick={() => setFacingMode((m) => (m === 'user' ? 'environment' : 'user'))} sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }} aria-label="Trocar câmera">
              <CameraswitchRoundedIcon />
            </IconButton>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button variant="contained" startIcon={<PhotoCameraRoundedIcon />} onClick={capturar} disabled={Boolean(erro)}>Capturar</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Botões "Câmera" e "Galeria". Usa getUserMedia quando disponível; senão abre a câmera nativa
 * do celular via <input capture>. Chama onPick(file) com a imagem escolhida.
 */
const PhotoPicker = ({ onPick, disabled, size = 'medium', fullWidth = false }) => {
  const galeriaRef = useRef(null);
  const cameraNativaRef = useRef(null);
  const [cameraAberta, setCameraAberta] = useState(false);
  const temCamera = typeof navigator !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getUserMedia;

  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (file) onPick(file);
  };

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ width: fullWidth ? '100%' : 'auto' }}>
        <Button
          variant="contained"
          size={size}
          fullWidth={fullWidth}
          startIcon={<PhotoCameraRoundedIcon />}
          disabled={disabled}
          onClick={() => (temCamera ? setCameraAberta(true) : cameraNativaRef.current.click())}
        >
          Câmera
        </Button>
        <Button variant="outlined" size={size} fullWidth={fullWidth} startIcon={<PhotoLibraryRoundedIcon />} disabled={disabled} onClick={() => galeriaRef.current.click()}>
          Galeria
        </Button>
      </Stack>
      <input ref={galeriaRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleFile} style={{ display: 'none' }} />
      <input ref={cameraNativaRef} type="file" accept="image/*" capture="user" onChange={handleFile} style={{ display: 'none' }} />
      <CameraDialog
        open={cameraAberta}
        onClose={() => setCameraAberta(false)}
        onCapture={(file) => {
          setCameraAberta(false);
          onPick(file);
        }}
      />
    </>
  );
};

export default PhotoPicker;
